import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { forkJoin, Observable, of } from "rxjs";
import { Photo } from "../_models/Photo";
import { ImgMaxPXSizeService } from "./img-maxpx-size.service";
import { JWTTokenService } from "./jwtToken.service";
import { Ng2ImgMaxService } from "./ng2-img-max.service";
import { Ng2PicaService } from "./ng2-pica.service";
import { ImageProcessingStage, PhotoService } from "./photo.service";
import { S3Service } from "./s3.service";
import { getUrl } from '../_shared/utils';
import { map, mergeMap, tap, take } from "rxjs/operators";
import { CueConfig } from "../_models/CueConfig";


@Injectable()
export class CueAppService extends PhotoService {

    constructor(
        protected http: HttpClient,
        protected ng2ImgToolsService: Ng2ImgMaxService, 
        protected s3Service: S3Service,
        protected imgMaxSizeService: ImgMaxPXSizeService,
        protected ng2PicaService: Ng2PicaService,
        protected sanitizer: DomSanitizer,
        protected jwtTokenService: JWTTokenService) {

        super(http, ng2ImgToolsService, s3Service, imgMaxSizeService, ng2PicaService, sanitizer, jwtTokenService);
    }

    public createNewApp(app: any) {

        return this.http.post<any>(getUrl('app/create'), app, this.requestOptionsAuthenticated());
    }

    public getConfigs() : Observable<any> {

      return this.CachedHttpGet<any>('app/config');
    }

    public getConfigById(id: number) : Observable<CueConfig> {

      return this.HttpGet<CueConfig>(`app/config/${id}`);
    }

    public updateConfig(config: CueConfig) : Observable<any> {

      return this.HttpPost<any>('app/config/update', config);
    }

    public commitUploads(): Observable<any> {

        console.log('Commit called, waiting for all resize operations to complete...');
    
        let completed = 0;
    
        const locations = Object.keys(this.resizeObservablesDict);
    
        const uploads$ = locations
          .map(
            loc => {
    
              const resizeObservable = this.resizeObservablesDict[loc];
              // phase 3: when resizing and commitCalled are both complete, continue with upload
              return resizeObservable
                .pipe(mergeMap(
                  res => {
    
                    console.log('Uploading resized file: ' + res.file.name);

                    return this.registerUploadedPhoto(res.file, 1, loc)
                      .pipe(mergeMap(
                        photo => {

                            const getPresignedUrlEndpoint = photo.folderName.includes('headerImage') ? "get-branding-signed-s3-url" : "get-android-builds-signed-s3-url";

                            const headers = getPresignedUrlEndpoint.includes('branding') ? { 'x-amz-acl': 'public-read' } : {}
                            return this.s3Service.uploadFile(res.file, photo.folderName, getPresignedUrlEndpoint, undefined, headers);
                        }))
                        .pipe(map(
                            uploadUrl => {
                            
                            const cdnDomain = uploadUrl.includes('headerImage') ? "https://cuebranding.cueaudio.com" : "https://cueandroidbuilds.cueaudio.com";
                            
                            console.log(`Uploded ${++completed}/${locations.length} images`);
                            // once uploaded, provide the permanent url
                            res.subscriber.next({
                                name: res.file.name,
                                url: cdnDomain + this.s3Service.getUrlWithoutSignature(uploadUrl.split('s3.amazonaws.com')[1]),
                                location: loc,
                                stage: ImageProcessingStage.Uploaded
                            });
                        }
                        ));
                  }
                ));
          }
        );
    
        return  (uploads$.length > 0 ? forkJoin(uploads$) : of(null)).pipe(tap(_ => {
          this.clearQueue();
          console.log('Uploading images complete');
        }));
      }

    protected override registerUploadedPhoto(file: File, imageType: number, filePath?: string): Observable<Photo> {
        
        const lastSlashIndex = filePath.lastIndexOf('/');
        const name = filePath.substring(lastSlashIndex + 1);

        return this.http.post<Photo>(getUrl('app/photo/add'), { name, filePath: filePath.substring(0, lastSlashIndex)}, this.requestOptionsAuthenticated());
    }

    public uploadAndroidAppIcons(file: File, applicationName: string) : Observable<any> {

        const hdpi = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/android/mipmap-hdpi/ic_launcher.png`, {width: 72, height: 72}, true, true).pipe(take(1));

        const mdpi = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/android/mipmap-mdpi/ic_launcher.png`, {width: 48, height: 48}, true, true).pipe(take(1));

        const xhdpi = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/android/mipmap-xhdpi/ic_launcher.png`, {width: 96, height: 96}, true, true).pipe(take(1));

        const xxhdpi = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/android/mipmap-xxhdpi/ic_launcher.png`, {width: 144, height: 144}, true, true).pipe(take(1));

        const xxxhdpi = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/android/mipmap-xxxhdpi/ic_launcher.png`, {width: 192, height: 192}, true, true).pipe(take(1));

        return forkJoin([hdpi, mdpi, xhdpi, xxhdpi, xxxhdpi]);
    }

    public uploadIosAppIcons(file: File, applicationName: string) : Observable<any> {

        const pix20 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/20.png`, {width: 20, height: 20}, true, true).pipe(take(1));

        const pix29 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/29.png`, {width: 29, height: 29}, true, true).pipe(take(1));

        const pix40 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/40.png`, {width: 40, height: 40}, true, true).pipe(take(1));

        const pix50 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/50.png`, {width: 50, height: 50}, true, true).pipe(take(1));

        const pix57 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/57.png`, {width: 57, height: 57}, true, true).pipe(take(1));

        const pix58 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/58.png`, {width: 58, height: 58}, true, true).pipe(take(1));

        const pix60 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/60.png`, {width: 60, height: 60}, true, true).pipe(take(1));

        const pix72 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/72.png`, {width: 72, height: 72}, true, true).pipe(take(1));

        const pix76 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/76.png`, {width: 76, height: 76}, true, true).pipe(take(1));

        const pix80 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/80.png`, {width: 80, height: 80}, true, true).pipe(take(1));

        const pix87 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/87.png`, {width: 87, height: 87}, true, true).pipe(take(1));

        const pix100 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/100.png`, {width: 100, height: 100}, true, true).pipe(take(1));

        const pix114 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/114.png`, {width: 114, height: 114}, true, true).pipe(take(1));

        const pix120 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/120.png`, {width: 120, height: 120}, true, true).pipe(take(1));

        const pix144 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/144.png`, {width: 144, height: 144}, true, true).pipe(take(1));

        const pix152 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/152.png`, {width: 152, height: 152}, true, true).pipe(take(1));

        const pix167 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/167.png`, {width: 167, height: 167}, true, true).pipe(take(1));

        const pix180 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/180.png`, {width: 180, height: 180}, true, true).pipe(take(1));

        const pix1024 = this.enqueueUploadPhoto(file, `resources/${applicationName}/app_icons/Assets.xcassets/AppIcon.appiconset/1024.png`, {width: 1024, height: 1024}, true, true).pipe(take(1));

        return forkJoin([pix20, pix29, pix40, pix50, pix57, pix58, pix60, pix72, pix76, pix80, pix87, pix100, pix114, pix120, pix144, pix152, pix167, pix180, pix1024]);
    }

    public getLatestAndroidVersion() : Observable<any> {

      return this.http.get<any>(getUrl('app/latest-android'), this.requestOptionsAuthenticated());
    }

    public getLatestIosVersion() : Observable<any> {

      return this.http.get<any>(getUrl('app/latest-ios'), this.requestOptionsAuthenticated());
    }
}